import { useState } from "react";
import { Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/States";

export const FINISHES = [
  ["limewash-oak", "Limewashed oak", "#d9c9ae"], ["matte-black", "Matte black", "#1f1f1f"], ["sage-milk", "Sage milk paint", "#9aa98c"],
  ["walnut", "Dark walnut", "#5a3a22"], ["whitewash-pine", "Whitewashed pine", "#ece4d6"], ["navy", "Navy lacquer", "#1f2d4a"],
  ["raw-wood", "Raw wood", "#c7a67a"], ["driftwood", "Driftwood grey", "#9d968b"],
];

export default function ReimagineDialog({ boardId, el, onClose, onPin }) {
  const [finish, setFinish] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [count, setCount] = useState(2);
  const [name, setName] = useState(el.data?.name || "");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const source = el.data?.url;

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/boards/${boardId}/ai/reimagine`, { source_url: source, finish, prompt, count, name });
      setResults(data.images);
      if (!data.images.length) toast.message("Nothing came back, try another finish");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const label = FINISHES.find((f) => f[0] === finish)?.[1] || prompt.slice(0, 40) || "Custom";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="reimagine-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-serif text-3xl font-normal"><Wand2 className="h-6 w-6 text-accent" />Reimagine this piece</DialogTitle></DialogHeader>
        <div className="grid gap-6 sm:grid-cols-[220px_1fr]">
          <div className="space-y-2">
            <img src={fileUrl(source)} alt="" className="aspect-square w-full rounded-lg object-cover shadow" data-testid="reimagine-source" />
            <p className="text-center text-xs text-muted-foreground">Original</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Piece name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Grandmother's dresser" data-testid="reimagine-name-input" /></div>
            <div className="space-y-1.5">
              <Label>Finish</Label>
              <div className="flex flex-wrap gap-2">
                {FINISHES.map(([k, l, hex]) => (
                  <button type="button" key={k} onClick={() => setFinish(finish === k ? null : k)} data-testid={`finish-${k}`}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${finish === k ? "border-primary bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                    <span className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ background: hex }} />{l}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Or describe the change</Label><Textarea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Two-tone: natural top, black base. Swap knobs for leather pulls." data-testid="reimagine-prompt-input" /></div>
            <div className="flex items-center gap-3">
              <Label className="shrink-0">Variations</Label>
              <div className="inline-flex rounded-full border p-0.5">{[1, 2, 3].map((n) => <button type="button" key={n} onClick={() => setCount(n)} className={`h-7 w-8 rounded-full text-xs ${count === n ? "bg-primary text-primary-foreground" : ""}`} data-testid={`reimagine-count-${n}`}>{n}</button>)}</div>
              <Button className="ml-auto rounded-full" onClick={run} disabled={busy || (!finish && !prompt.trim())} data-testid="reimagine-generate-button">{busy ? <Spinner /> : <Sparkles className="mr-2 h-4 w-4" />}{busy ? "Imagining…" : "Generate"}</Button>
            </div>
          </div>
        </div>
        {busy && <p className="text-center text-xs text-muted-foreground">This takes about 15–30 seconds per variation.</p>}
        {results.length > 0 && (
          <div className="space-y-3">
            <p className="eyebrow">Results · {label}</p>
            <div className="grid gap-4 sm:grid-cols-3" data-testid="reimagine-results">
              {results.map((r) => (
                <div key={r.file_id} className="space-y-2">
                  <img src={fileUrl(r.url)} alt="" className="aspect-square w-full rounded-lg object-cover shadow" data-testid="reimagine-result-image" />
                  <div className="flex gap-1.5">
                    <Button size="sm" className="flex-1 rounded-full" onClick={() => { onPin({ type: "furniture", w: 260, h: 300, data: { ...el.data, name: name || el.data?.name, url: r.url, before_url: source, finish: label, ai: true, link: el.data?.link } }); onClose(); }} data-testid="reimagine-pin-before-after">Pin before / after</Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => { onPin({ type: "image", w: 260, h: 240, data: { url: r.url, caption: `${name || "Piece"} · ${label}` } }); }} data-testid="reimagine-pin-image">Pin</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
